import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  BellRinging, PaperPlaneTilt, X, CheckCircle, WarningCircle,
  UserCircle, Devices, Info,
} from "@phosphor-icons/react";

const ROLE_LABEL = {
  admin: "Admin",
  user: "Team",
  client_poc: "Client POC",
};

const ROLE_TONE = {
  admin: "text-brand-cyan border-brand-cyan/40 bg-brand-cyan/5",
  user: "text-slate-200 border-slate-500/40 bg-slate-500/5",
  client_poc: "text-amber-300 border-amber-400/40 bg-amber-400/5",
};

export default function ClientPushTestModal({ client, onClose }) {
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [audience, setAudience] = useState({
    include_admins: true,
    include_users: true,
    include_client_pocs: true,
  });
  const [title, setTitle] = useState(`[TEST] ${client.name} — escalation drill`);
  const [body, setBody] = useState(
    `This is a test alert to confirm push notifications reach the on-call rotation for ${client.name}. No action required.`,
  );

  const fetchPreview = async (aud = audience) => {
    setLoadingPreview(true);
    setError("");
    try {
      const { data } = await api.get(`/clients/${client.id}/push-recipients`, {
        params: aud,
      });
      setPreview(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, []);

  const toggleAudience = (key) => {
    const next = { ...audience, [key]: !audience[key] };
    setAudience(next);
    setResult(null);
    fetchPreview(next);
  };

  const sendTest = async () => {
    setSending(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post(`/clients/${client.id}/push-test`, {
        title: title || null,
        body: body || null,
        ...audience,
      });
      setResult(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const totalRecipients = preview?.total_recipients ?? 0;
  const totalDevices = preview?.total_devices ?? 0;
  const canSend = !sending && !loadingPreview && totalRecipients > 0;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="client-push-test-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="sw-card w-full max-w-2xl p-6 space-y-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg border border-brand-cyan/40 bg-brand-cyan/10 flex items-center justify-center">
              <BellRinging size={20} weight="bold" className="text-brand-cyan" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight" data-testid="client-push-test-title">
                Test escalation push — {client.name}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Fires a real push notification to every on-call recipient with a registered device.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="client-push-test-close"
            className="p-1.5 text-slate-300 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        {/* Audience selector */}
        <div className="space-y-2">
          <label className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
            Audience
          </label>
          <div className="flex flex-wrap gap-2">
            <AudienceChip
              testid="aud-admins"
              checked={audience.include_admins}
              onClick={() => toggleAudience("include_admins")}
              label="MSP Admins"
            />
            <AudienceChip
              testid="aud-users"
              checked={audience.include_users}
              onClick={() => toggleAudience("include_users")}
              label="MSP Team"
            />
            <AudienceChip
              testid="aud-client-pocs"
              checked={audience.include_client_pocs}
              onClick={() => toggleAudience("include_client_pocs")}
              label="Client POCs"
            />
          </div>
        </div>

        {/* Message */}
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="client-push-title-input"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">
              Body
            </label>
            <textarea
              rows={2}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="client-push-body-input"
              className="w-full"
            />
          </div>
        </div>

        {/* Recipients preview */}
        <div className="rounded-lg border border-border bg-slate-950/40 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
              Recipients
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1" data-testid="recipient-count">
                <UserCircle size={12} weight="bold" /> {totalRecipients} users
              </span>
              <span className="inline-flex items-center gap-1" data-testid="device-count">
                <Devices size={12} weight="bold" /> {totalDevices} devices
              </span>
            </div>
          </div>

          {loadingPreview ? (
            <div className="text-xs text-slate-500 py-2">Resolving on-call rotation…</div>
          ) : totalRecipients === 0 ? (
            <div className="flex items-start gap-2 text-xs text-amber-300 py-2" data-testid="no-recipients-note">
              <Info size={14} weight="bold" className="mt-0.5 shrink-0" />
              <div>
                No selected recipients have registered a push subscription yet.
                Ask them to open the app once and allow notifications, or install
                the native mobile app.
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-border/50">
              {(preview?.recipients || []).map((r) => (
                <li
                  key={r.user_id}
                  className="py-2 flex items-center justify-between gap-3"
                  data-testid={`recipient-row-${r.user_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] border rounded ${ROLE_TONE[r.role] || ROLE_TONE.user}`}
                    >
                      {ROLE_LABEL[r.role] || r.role}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-100 truncate">{r.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{r.email}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-400 whitespace-nowrap">
                    {r.sub_count} device{r.sub_count === 1 ? "" : "s"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div
            className="flex items-start gap-2 text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded p-2"
            data-testid="client-push-error"
          >
            <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {result && (
          <div
            className="rounded-lg border border-brand-cyan/30 bg-brand-cyan/5 p-3 space-y-1"
            data-testid="client-push-result"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-brand-cyan">
              <CheckCircle size={16} weight="bold" />
              Dispatched to {result.total_recipients} user{result.total_recipients === 1 ? "" : "s"} · {result.total_devices} device{result.total_devices === 1 ? "" : "s"}
            </div>
            <div className="text-[11px] text-slate-300">
              {result.total_success} succeeded · {result.total_failed} failed
              {result.total_removed ? ` · ${result.total_removed} expired token${result.total_removed === 1 ? "" : "s"} pruned` : ""}
            </div>
            {result.total_devices === 0 && (
              <div className="text-[11px] text-amber-300 pt-1">
                No devices received the notification — no push subscriptions on the selected audience.
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            data-testid="client-push-cancel-btn"
            className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] border border-border hover:border-slate-400 rounded text-slate-200"
          >
            Close
          </button>
          <button
            onClick={sendTest}
            disabled={!canSend}
            data-testid="client-push-send-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] border border-brand-cyan/50 bg-brand-cyan/15 text-brand-cyan hover:bg-brand-cyan/25 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {sending ? "Sending…" : "Send test push"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AudienceChip({ checked, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] border rounded transition-colors ${
        checked
          ? "border-brand-cyan/50 bg-brand-cyan/10 text-brand-cyan"
          : "border-border text-slate-400 hover:text-slate-200 hover:border-slate-400"
      }`}
    >
      <span
        className={`w-2 h-2 rounded-sm ${checked ? "bg-brand-cyan" : "bg-slate-700"}`}
      />
      {label}
    </button>
  );
}
